import { useCallback, useRef, useState } from "react";

export type ColumnWidths = Record<string, number>;

/**
 * Provides resizable column widths with localStorage persistence.
 *
 * @param storageKey   Key used for localStorage persistence.
 * @param defaults     Default widths (pixel values) keyed by column name.
 * @param minWidth     Minimum column width in pixels (default: 60).
 */
export function useColumnResize(
    storageKey: string,
    defaults: ColumnWidths,
    minWidth = 60,
) {
    const [widths, setWidths] = useState<ColumnWidths>(() => {
        if (typeof window === "undefined") return defaults;
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return defaults;
            const saved = JSON.parse(raw) as ColumnWidths;
            // Merge: saved widths take precedence, fallback to defaults for missing keys
            return { ...defaults, ...saved };
        } catch {
            return defaults;
        }
    });

    // Keep a stable ref so the startResize closure always reads the latest widths
    const widthsRef = useRef(widths);
    widthsRef.current = widths;

    // Keep a stable ref for defaults so startResize closure doesn't need it in deps
    const defaultsRef = useRef(defaults);
    defaultsRef.current = defaults;

    /**
     * Call from onMouseDown of a resize handle element.
     * @param key  The column key (must match a key in `defaults`).
     * @param e    The mouse event.
     */
    const startResize = useCallback(
        (key: string, e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const startW =
                widthsRef.current[key] ??
                defaultsRef.current[key] ??
                minWidth;
            const startX = e.clientX;

            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";

            const onMove = (ev: MouseEvent) => {
                const delta = ev.clientX - startX;
                const next = Math.max(minWidth, startW + delta);
                setWidths((prev) => {
                    const updated = { ...prev, [key]: next };
                    try {
                        localStorage.setItem(storageKey, JSON.stringify(updated));
                    } catch {}
                    return updated;
                });
            };

            const onUp = () => {
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
                window.removeEventListener("mousemove", onMove);
                window.removeEventListener("mouseup", onUp);
            };

            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp);
        },
        // storageKey and minWidth are the only truly external dependencies;
        // widths/defaults are read via refs to avoid stale closures.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [storageKey, minWidth],
    );

    /** Returns the width for a given key, falling back to the default. */
    function w(key: string): number {
        return widths[key] ?? defaults[key] ?? minWidth;
    }

    return { widths, setWidths, startResize, w };
}
